/*********************
 * Heuristic Transformer
 *
 * Any data transformations necessary to prep input from lexa-app
 * for the heuristic policy go here
 *********************/
import { interventionSituationTypes } from "./constants";
import { RankedInterventionSelector } from "heuristic-policy/intervention_selector/ranked_intervention_selector/RankedInterventionSelector";
import { createSelectedWordFields } from "heuristic-policy/utils/metadataUtils";

// We want one RankedInterventionSelector as a singleton for the lifetime of the activity or session
const rankedInterventionSelector = new RankedInterventionSelector();

export default function transformState(state, internalState) {
  let transformedStateAcc = {}; // transformed state accumulator
  try {
    // Add EDM prediction confidence data for each word in the phrase

    // Note: if testing, confData can be added with additional attributes additional attributes
    /*
      transformedStateAcc.confData = state.confData;
    */
    let predConfsAsObjects = null;
    if (state.interactions.predictionConfidences) {
      // if this is a phrase level intervention
      predConfsAsObjects = [];
      state.interactions.predictionConfidences.forEach((predConf) => {
        predConfsAsObjects.push({ correct_confidences: predConf });
      });
    }
    transformedStateAcc.confData = predConfsAsObjects;

    transformedStateAcc.locale = state.locale || "en-us";
    transformedStateAcc.interventionFrequency = state.interventionFrequency || {
      //default to STANDARD frequency
      frustrationCurveSteepness: 1,
      frustrationThreshold: 3,
    };

    // Set student fields
    transformedStateAcc.grade = state.student.grade;
    transformedStateAcc.studentAREA = state.student.AREA;
    transformedStateAcc.wcpm = state.student.WCPM;
    transformedStateAcc.decodingPR = state.student.decodePR;
    transformedStateAcc.decodingScore = state.student.decodeScore;
    transformedStateAcc.isESL = state.student.isESL;
    transformedStateAcc.isEarlyReader = state.student.isEarlyReader;
    transformedStateAcc.isSpanishEarlyReader =
      state.student.isSpanishEarlyReader;

    createSelectedPhraseFields(state, transformedStateAcc);

    createDisabledInterventionsField(state, transformedStateAcc);

    // Add words metadata and errors
    setWordMetadataAndErrors(state, transformedStateAcc);

    // Add phrases and phrase index
    transformedStateAcc.phraseIndex =
      state.story.textMetadataForCurrentPhrase.phraseIndex;
    transformedStateAcc.phraseLength =
      state.story.textMetadataForCurrentPhrase.words.length;
    transformedStateAcc.storyPhrases = state.story.phrases;

    // FOR TESTING
    /*
    transformedStateAcc.storyId = state.story.storyId;
    transformedStateAcc.studentId = state.student.userId;
    */

    // Set word-level trigger fields
    let scope = state.interactions.scope.toUpperCase();
    if (
      scope == interventionSituationTypes.WORD ||
      scope == interventionSituationTypes.WORD_PREP
    ) {
      let index = state.interactions.currentWordIndex;
      if (scope == interventionSituationTypes.WORD_PREP) {
        if (state.story.textMetadataForCurrentPhrase.words[index]) {
          state.interactions.inWord =
            state.story.textMetadataForCurrentPhrase.words[index].WORD;
        }
        transformedStateAcc.wordPrep = true;
      }
      transformedStateAcc.endOfPhrase = 0;
      transformedStateAcc.word = state.interactions.inWord;
      transformedStateAcc.wordIndex = index;
      createSelectedWordFields(transformedStateAcc.word, transformedStateAcc);
      // Set phrase-level trigger fields
    } else {
      // (for now, assume all other actions are of situation type "phrase")
      transformedStateAcc.endOfPhrase = true;
      transformedStateAcc.rushingDetected = state.interactions.isRushing;
      transformedStateAcc.inErrorCount =
        state.interactions.errorsInPhrase.length;
      transformedStateAcc.skips = state.interactions.skips;
      transformedStateAcc.frameTimes = state.interactions.frameTimes;
      transformedStateAcc.phraseMetaData = state.interactions.phraseMetaData;
      transformedStateAcc.mostRecentAccuracy =
        state.interactions.mostRecentAccuracy;
    }

    // Add intervention history by phrase
    transformedStateAcc.interventionHistoryByPhrase =
      internalState.getInterventionHistoryByPhrase();

    // V2 intervention weights
    transformedStateAcc.phraseInterventionWeights =
      state.interactions.phraseInterventionWeights;

    // Pass through RankedInterventionSelector singleton through in state
    transformedStateAcc.rankedInterventionSelector = rankedInterventionSelector;
  } catch (err) {
    transformedStateAcc.error = err;
  }

  transformedStateAcc.isValidRunForV2 = checkIsValidRunForV2(
    state,
    transformedStateAcc
  );

  return transformedStateAcc;
}

function checkIsValidRunForV2(state, transformedStateAcc) {
  const {
    isEarlyReader: isEnglishEarlyReader,
    isSpanishEarlyReader,
    locale,
    wordIndex,
    endOfPhrase,
    phraseInterventionWeights,
  } = transformedStateAcc;

  const isEarlyReader =
    (locale.toLowerCase() === "en-us" && isEnglishEarlyReader) ||
    (locale.toLowerCase() === "es-mx" && isSpanishEarlyReader);

  const isSpanish = locale.toLowerCase() === "es-mx";

  const hasInterventionWeights =
    phraseInterventionWeights &&
    phraseInterventionWeights.length > 0 &&
    (phraseInterventionWeights.length > wordIndex ||
      phraseInterventionWeights.length >=
        state.story.textMetadataForCurrentPhrase.words.length);

  const validRunForV2 =
    endOfPhrase && !isSpanish && !isEarlyReader && hasInterventionWeights;

  return validRunForV2;
}

// Set corresponding phrase metadata fields for a phrase that we'll intervene on
function createSelectedPhraseFields(state, transformedStateAcc) {
  if (!(state && state.interactions && state.interactions.phraseMetaData)) {
    return;
  }

  if (state.interactions.phraseMetaData.questions) {
    transformedStateAcc["phrase_questions"] =
      state.interactions.phraseMetaData.questions;
  }

  if (state.interactions.phraseMetaData.predictionQuestions) {
    transformedStateAcc["phrase_predictionQuestions"] =
      state.interactions.phraseMetaData.predictionQuestions;
  }
}

// Set disabledIntervention field on the state based on the interaction data
function createDisabledInterventionsField(state, transformedStateAcc) {
  if (
    !(
      state &&
      state.interactions &&
      state.interactions.disabledInterventionTypes
    )
  ) {
    return;
  }

  transformedStateAcc["disabledInterventionTypes"] =
    state.interactions.disabledInterventionTypes;
}

function setWordMetadataAndErrors(state, transformedStateAcc) {
  // filter nulls out of phraseWordMetadata
  let validMetadata = [];
  let invalidMetadataIndices = [];
  state.story.textMetadataForCurrentPhrase.words.forEach(
    (wordMetadata, index) => {
      if (wordMetadata && wordMetadata.WORD) {
        validMetadata.push(wordMetadata);
      } else {
        validMetadata.push({
          WORD: "",
          PHON: null,
          NSYL: null,
          NLET: null,
          CONC: 0,
          SIGHT: 0,
          AREA_AOA: 0,
          AREA_discrim: null,
          IMG_URL: null,
          VIDEO_URL: null,
          RHYME: null,
          DEFINITION: null,
        });
        invalidMetadataIndices.push(index);
      }
    }
  );
  transformedStateAcc.phraseWordMetadata = validMetadata;
  if (
    state.interactions.scope.toUpperCase() == interventionSituationTypes.PHRASE
  ) {
    transformedStateAcc.errorsInPhrase =
      state.interactions.errorsInPhrase.filter((error) => {
        return !invalidMetadataIndices.includes(error.index);
      });
    transformedStateAcc.closeErrors = state.interactions.closeErrors.filter(
      (error) => {
        return !invalidMetadataIndices.includes(error.index);
      }
    );
  }
}
